import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import useSeo from "@/hooks/useSeo";
import { movies } from "@/mocks/movies";
import Breadcrumb from "@/components/base/Breadcrumb";
import FilterPanel from "@/components/feature/FilterPanel";
import MovieListItem from "@/components/base/MovieListItem";
import Pagination from "@/components/base/Pagination";
import {
  categoryMeta,
  emptyFilters,
  filterMovies,
  paginate,
  sortMovies,
  type FilterState,
  type SortKey,
} from "@/utils/media";

const PER_PAGE = 6;

export default function Category() {
  const { type = "tv" } = useParams();
  const meta = categoryMeta[type] ?? categoryMeta.tv;

  const [filters, setFilters] = useState<FilterState>(emptyFilters);
  const [sort, setSort] = useState<SortKey>("latest");
  const [page, setPage] = useState(1);

  useSeo({
    title: `${meta.title} - 我的老婆姐姐影视资讯`,
    description: `${meta.desc} 由我的老婆姐姐整理，提供剧情介绍、主演信息与播出动态。`,
    keywords: `${meta.title},${meta.title}推荐,我的老婆姐姐,影视资讯,剧情介绍`,
  });

  const result = useMemo(() => {
    const byType = movies.filter((m) => m.type === meta.type);
    const filtered = filterMovies(byType, filters);
    const sorted = sortMovies(filtered, sort);
    return paginate(sorted, page, PER_PAGE);
  }, [meta.type, filters, sort, page]);

  const updateFilters = (next: FilterState) => {
    setFilters(next);
    setPage(1);
  };

  const resetAll = () => {
    setFilters(emptyFilters);
    setPage(1);
  };

  const hasFilter =
    filters.genres.length > 0 || filters.region !== "" || filters.yearLabel !== "";

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-8">
      <Breadcrumb items={[{ label: meta.title }]} />

      <div className="mt-5">
        <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950">
          {meta.title}
        </h1>
        <p className="mt-2 text-sm md:text-base text-foreground-500 max-w-2xl leading-relaxed">
          {meta.desc}
        </p>
      </div>

      <div className="mt-6 flex flex-col lg:flex-row gap-6 lg:gap-8">
        <div className="flex-1 min-w-0 order-2 lg:order-1">
          <div className="flex items-center justify-between gap-4 mb-4">
            <p className="text-sm text-foreground-500">
              共 <span className="text-foreground-900 font-medium">{result.total}</span> 部作品
            </p>
            <div className="flex items-center gap-1 p-1 bg-background-100 rounded-full">
              {(
                [
                  { key: "latest", label: "最新发布" },
                  { key: "hot", label: "热度排行" },
                ] as const
              ).map((s) => (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => {
                    setSort(s.key);
                    setPage(1);
                  }}
                  className={`px-3 py-1.5 rounded-full text-xs md:text-sm cursor-pointer transition-colors whitespace-nowrap ${
                    sort === s.key
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-600 hover:text-foreground-900"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {result.items.length > 0 ? (
            <div className="space-y-3 md:space-y-4">
              {result.items.map((m) => (
                <MovieListItem key={m.id} movie={m} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center text-center py-20 bg-background-100 rounded-lg border border-background-200">
              <span className="w-14 h-14 flex items-center justify-center rounded-full bg-background-200 text-foreground-400 text-2xl mb-4">
                <i className="ri-emotion-sad-line"></i>
              </span>
              <p className="text-foreground-800 font-medium">无相关影视内容，请调整筛选条件</p>
              <p className="mt-1 text-sm text-foreground-500">
                试试减少筛选条件，或查看更多分类
              </p>
              <button
                type="button"
                onClick={resetAll}
                className="mt-5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-label hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                重置筛选条件
              </button>
            </div>
          )}

          {result.items.length > 0 && (
            <Pagination page={result.page} totalPages={result.totalPages} onChange={setPage} />
          )}
        </div>

        <aside className="w-full lg:w-72 lg:shrink-0 order-1 lg:order-2">
          <div className="lg:sticky lg:top-20">
            <FilterPanel filters={filters} onChange={updateFilters} onReset={resetAll} />
            {hasFilter && (
              <p className="mt-3 text-xs text-foreground-400 text-center">
                已应用筛选条件，可随时重置
              </p>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}